import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import InvoiceUploader from './InvoiceUploader';
import UploadStatusList from './UploadStatusList';
import useUploadProgress from './useUploadProgress';
import { SETTLED_UPLOAD_KEYS, uploadStage } from './invoiceStatus';
import InvoiceList, { LIST_TABS, rowsForTab } from './InvoiceList';
import InvoiceReview from './InvoiceReview';
import DemoResetButton, { useDemoTools } from './DemoControls';
import useInvoiceLists from './useInvoiceLists';
import SetAsideEmails from './SetAsideEmails';
import MailboxPanel from './MailboxPanel';
import { canDo, useExperience } from '../experience/experience';

import { loadUploads, saveUploads } from './recentUploads';

export default function InvoicesPage() {
  const { invoiceId, id } = useParams();
  if (invoiceId || id) return <InvoiceReview invoiceId={invoiceId || id} />;
  return <InvoicesHome />;
}

function InvoicesHome() {
  const lists = useInvoiceLists();
  const demo = useDemoTools();
  const exp = useExperience();
  const canUpload = canDo(exp, 'invoices.upload');
  const canDecide = canDo(exp, 'invoices.decide');
  const [uploads, setUploads] = useState(loadUploads);
  const [picked, setPicked] = useState(null);
  const firstFull = LIST_TABS.find((t) => rowsForTab(lists.byState, t.id).length > 0);
  const tab = picked || firstFull?.id || 'needs_review';

  useEffect(() => {
    saveUploads(uploads);
  }, [uploads]);

  const progress = useUploadProgress(uploads, lists.byDoc);
  const unresolved = uploads.some((u) => u.documentId && !lists.byDoc[u.documentId]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!unresolved) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);   // the elapsed time on a Reading row counts live
    return () => clearInterval(t);
  }, [unresolved]);

  // A row whose file is posted, rejected or gone needs nothing more from the person: it clears itself shortly,
  // so a finished file is not left looking busy among the ones still being read.
  const clearing = useRef({});
  useEffect(() => {
    uploads.forEach((u) => {
      if (clearing.current[u.key]) return;
      if (!SETTLED_UPLOAD_KEYS.includes(uploadStage(u, lists.byDoc, Date.now(), progress).key)) return;
      clearing.current[u.key] = setTimeout(() => setUploads((prev) => prev.filter((x) => x.key !== u.key)), 8000);
    });
  }, [uploads, lists.byDoc, progress]);
  // Timers outlive a refresh (a refresh must not restart the 8 seconds); they stop only when the page goes.
  useEffect(() => () => Object.values(clearing.current).forEach(clearTimeout), []);

  // Mark rows as polled once a refresh has run after they were sent, so "Uploaded" becomes "Reading".
  useEffect(() => {
    setUploads((prev) => (prev.some((u) => !u.polled && u.documentId) ? prev.map((u) => ({ ...u, polled: true })) : prev));
  }, [lists.tick]);

  const onUploads = (rows) => {
    setUploads((prev) => [...rows, ...prev].slice(0, 20));
    lists.refresh();
    demo.refresh();
  };

  const afterReset = () => {
    setUploads([]);
    lists.refresh();
    demo.refresh();
  };

  return (
    <main className="min-h-screen bg-surface-muted" data-testid="invoices-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-overline text-ink-muted">Cut Cost</p>
            <h1 className="font-display text-3xl sm:text-4xl text-ink">Invoices</h1>
            <p className="text-ink-muted mt-1">Upload a supplier invoice, check what was read, then approve the new prices.</p>
          </div>
          <DemoResetButton info={demo.info} onDone={afterReset} />
        </header>
        {canUpload && <InvoiceUploader onUploads={onUploads} />}
        <UploadStatusList uploads={uploads} invoicesByDoc={lists.byDoc} progress={progress} now={now}
          onDismiss={(key) => setUploads((prev) => prev.filter((u) => u.key !== key))} />
        {canDecide && <MailboxPanel tick={lists.tick} onChanged={lists.refresh} />}
        {canDecide && <SetAsideEmails tick={lists.tick} onChanged={lists.refresh} />}
        <InvoiceList byState={lists.byState} tab={tab} onTab={setPicked} loaded={lists.loaded} errors={lists.errors} onRetry={lists.refresh} />
      </div>
    </main>
  );
}
